import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import AnimatedSection from '../../components/common/AnimatedSection';

// 样式组件
const TeamContainer = styled('div', {
  padding: '0 20px',
});

const TeamMembers = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '30px',
  margin: '40px 0',
});

const TeamMemberCard = styled('div', {
  background: '$bgSecondary',
  borderRadius: '15px',
  padding: '30px',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
  textAlign: 'center',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)',
  },
  
  '& .member-photo': {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    margin: '0 auto 20px',
    background: 'linear-gradient(145deg, #F0F4FF 0%, #E5ECFF 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '40px',
  },
  
  '& .member-name': {
    fontSize: '22px',
    fontWeight: '600',
    marginBottom: '5px',
    color: '$headingColor',
  },
  
  '& .member-position': {
    fontSize: '16px',
    color: '#3563E9',
    marginBottom: '15px',
  },
  
  '& .member-bio': {
    fontSize: '15px',
    color: '$textSecondary',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  
  '& .member-expertise': {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '15px',
  },
  
  '& .expertise-tag': {
    padding: '5px 12px',
    borderRadius: '20px',
    background: '#F0F4FF',
    color: '#3563E9',
    fontSize: '14px',
    fontWeight: '500',
  }
});

const TeamAchievements = styled('div', {
  background: 'linear-gradient(145deg, #F0F4FF 0%, #E5ECFF 100%)',
  borderRadius: '15px',
  padding: '40px',
  margin: '40px 0',
});

const AchievementItem = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '25px',
  
  '& .achievement-icon': {
    fontSize: '24px',
    color: '#3563E9',
    marginRight: '15px',
    marginTop: '3px',
  },
  
  '& .achievement-content': {
    flex: 1,
  },
  
  '& .achievement-title': {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '$headingColor',
  },
  
  '& .achievement-description': {
    fontSize: '16px',
    color: '$textSecondary',
    lineHeight: '1.6',
  }
});

// AI技术团队页面组件
const AiTeam: React.FC = () => {
  const { t } = useTranslation();
  
  // 示例团队成员数据
  const teamMembers = [
    {
      name: t('ai.team.member1.name'),
      position: t('ai.team.member1.position'),
      bio: t('ai.team.member1.bio'),
      photo: '👨‍💻',
      expertise: [
        t('ai.team.member1.expertise1'),
        t('ai.team.member1.expertise2'),
        t('ai.team.member1.expertise3')
      ]
    },
    {
      name: t('ai.team.member2.name'),
      position: t('ai.team.member2.position'),
      bio: t('ai.team.member2.bio'),
      photo: '👩‍💻',
      expertise: [
        t('ai.team.member2.expertise1'),
        t('ai.team.member2.expertise2'),
        t('ai.team.member2.expertise3')
      ]
    },
    {
      name: t('ai.team.member3.name'),
      position: t('ai.team.member3.position'),
      bio: t('ai.team.member3.bio'),
      photo: '👨‍🔬',
      expertise: [
        t('ai.team.member3.expertise1'),
        t('ai.team.member3.expertise2'),
        t('ai.team.member3.expertise3')
      ]
    },
    {
      name: t('ai.team.member4.name'),
      position: t('ai.team.member4.position'),
      bio: t('ai.team.member4.bio'),
      photo: '👩‍🔬',
      expertise: [
        t('ai.team.member4.expertise1'),
        t('ai.team.member4.expertise2'),
        t('ai.team.member4.expertise3')
      ]
    }
  ];
  
  // 示例团队成就数据
  const teamAchievements = [
    {
      icon: '🏆',
      title: t('ai.team.achievement1.title'),
      description: t('ai.team.achievement1.description')
    },
    {
      icon: '📊',
      title: t('ai.team.achievement2.title'),
      description: t('ai.team.achievement2.description')
    },
    {
      icon: '💡',
      title: t('ai.team.achievement3.title'),
      description: t('ai.team.achievement3.description')
    },
    {
      icon: '🔍',
      title: t('ai.team.achievement4.title'),
      description: t('ai.team.achievement4.description')
    }
  ];
  
  return (
    <TeamContainer>
      <AnimatedSection direction="up" delay={0.2}>
        <h2 className="section-title">{t('ai.team.title')}</h2>
        <p className="section-subtitle">{t('ai.team.subtitle')}</p>
      </AnimatedSection>
      
      <AnimatedSection direction="up" delay={0.3}>
        <TeamMembers>
          {teamMembers.map((member, index) => (
            <TeamMemberCard key={index}>
              <div className="member-photo">{member.photo}</div>
              <div className="member-name">{member.name}</div>
              <div className="member-position">{member.position}</div>
              <div className="member-bio">{member.bio}</div>
              <div className="member-expertise">
                {member.expertise.map((item, i) => (
                  <span key={i} className="expertise-tag">{item}</span>
                ))}
              </div>
            </TeamMemberCard>
          ))}
        </TeamMembers>
      </AnimatedSection>
      
      <AnimatedSection direction="up" delay={0.4}>
        <h3 className="section-subtitle">{t('ai.team.achievementsTitle')}</h3>
        <TeamAchievements>
          {teamAchievements.map((achievement, index) => (
            <AchievementItem key={index}>
              <div className="achievement-icon">{achievement.icon}</div>
              <div className="achievement-content">
                <div className="achievement-title">{achievement.title}</div>
                <div className="achievement-description">{achievement.description}</div>
              </div>
            </AchievementItem>
          ))}
        </TeamAchievements>
      </AnimatedSection>
    </TeamContainer>
  );
};

export default AiTeam; 